/**
 * Date-only values (`YYYY-MM-DD`) as `Temporal.PlainDate`, a day with no time
 * or zone. `Date` reads such a value as UTC midnight, so formatting or
 * comparing it anywhere behind UTC names the day before.
 *
 * `YYYY-MM-DD` stays the wire and storage format; convert at the boundary.
 * Instants (`createdAt`, `approvedAt`) stay `Date` or ISO strings, and become
 * a calendar date only through `calendarDateOf`, which names the zone.
 *
 * @module utils/calendar-dates
 */
import { Temporal } from "temporal-polyfill";

export type CalendarDate = Temporal.PlainDate;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a `YYYY-MM-DD` value, refusing any other shape and any day the
 * calendar does not have (`2026-02-30`).
 *
 * @param value - The date as written on the wire.
 * @returns The date, or `undefined` when `value` names no real day.
 */
export function parseCalendarDate(value: string): CalendarDate | undefined {
  if (!DATE_ONLY.test(value)) {
    return undefined;
  }
  try {
    return Temporal.PlainDate.from(value, { overflow: "reject" });
  } catch {
    return undefined;
  }
}

/**
 * Whether a value is a `YYYY-MM-DD` date naming a real day.
 *
 * @param value - The candidate.
 * @returns True when `parseCalendarDate` accepts it.
 */
export function isCalendarDate(value: string): boolean {
  return parseCalendarDate(value) !== undefined;
}

/**
 * Format a calendar date for display, in the day it names.
 *
 * A string that is not a date-only value is handed back untouched rather than
 * guessed at, so a malformed value shows as itself instead of as another day.
 *
 * @param value - The date, as `YYYY-MM-DD` or a parsed date.
 * @param locale - The locale to format in.
 * @returns The formatted day, or `value` when it is not a calendar date.
 */
export function formatCalendarDate(value: string | CalendarDate, locale = "en-IE"): string {
  const date = typeof value === "string" ? parseCalendarDate(value) : value;
  if (!date) {
    return String(value);
  }
  // UTC on both sides, so the zone cannot move the day the date names.
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeZone: "UTC" }).format(
    Date.UTC(date.year, date.month - 1, date.day),
  );
}

/**
 * Add whole years. 29 February lands on 28 February in a common year.
 *
 * @param date - The starting day.
 * @param years - Years to add; negative subtracts.
 * @returns The resulting day.
 */
export function addYears(date: CalendarDate, years: number): CalendarDate {
  return date.add({ years }, { overflow: "constrain" });
}

/**
 * Order two calendar dates.
 *
 * @param left - The first date.
 * @param right - The second date.
 * @returns Negative, zero or positive, as for `Array.prototype.sort`.
 */
export function compareDates(left: CalendarDate, right: CalendarDate): number {
  return Temporal.PlainDate.compare(left, right);
}

/**
 * Whole days from one date to another.
 *
 * @param from - The earlier date.
 * @param to - The later date.
 * @returns Days elapsed; negative when `to` is before `from`.
 */
export function daysBetween(from: CalendarDate, to: CalendarDate): number {
  return from.until(to, { largestUnit: "days" }).days;
}

/**
 * The zone the current runtime reads local time in: the viewer's, in a browser.
 *
 * @returns An IANA zone identifier.
 */
export function viewerTimeZone(): string {
  return Temporal.Now.timeZoneId();
}

/**
 * The day an instant falls on in a named zone.
 *
 * @param instant - A `Date` or ISO 8601 timestamp carrying an offset.
 * @param timeZone - IANA zone the day is read in.
 * @returns The calendar date there.
 */
export function calendarDateOf(instant: Date | string, timeZone: string): CalendarDate {
  const resolved =
    typeof instant === "string"
      ? Temporal.Instant.from(instant)
      : Temporal.Instant.fromEpochMilliseconds(instant.getTime());
  return resolved.toZonedDateTimeISO(timeZone).toPlainDate();
}

/**
 * Format the day an instant falls on, by default in the viewer's zone.
 *
 * @param instant - ISO 8601 timestamp carrying an offset.
 * @param locale - The locale to format in.
 * @param timeZone - IANA zone the day is read in.
 * @returns The formatted day, or `instant` when it is not a timestamp.
 */
export function formatDayOf(
  instant: string,
  locale = "en-IE",
  timeZone = viewerTimeZone(),
): string {
  try {
    return formatCalendarDate(calendarDateOf(instant, timeZone), locale);
  } catch {
    return instant;
  }
}

/**
 * Today in a named zone.
 *
 * @param timeZone - IANA zone the day is read in.
 * @param now - The current instant, for callers holding an injected clock.
 * @returns Today's calendar date there.
 */
export function todayIn(timeZone: string, now?: Date): CalendarDate {
  return now ? calendarDateOf(now, timeZone) : Temporal.Now.plainDateISO(timeZone);
}

/**
 * The UTC-midnight `Date` a SQL `date` parameter binds a calendar date as.
 *
 * @param value - The date, as `YYYY-MM-DD` or a parsed date.
 * @returns The instant at 00:00 UTC on that day.
 */
export function utcMidnightOf(value: string | CalendarDate): Date {
  const date = typeof value === "string" ? Temporal.PlainDate.from(value) : value;
  return new Date(date.toZonedDateTime("UTC").epochMilliseconds);
}
